// Install prompts (rebuild.md §8.10, task 8.10). Chromium browsers fire `beforeinstallprompt`: it is held and
// offered as an "Install the game" button. iOS Safari has no prompt, so Settings explains Share → Add to Home
// Screen. Nothing is shown once the game runs installed.
import { signal } from '@preact/signals';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** True while the browser has an install prompt waiting. */
export const installAvailable = signal(false);
let deferred: BeforeInstallPromptEvent | null = null;

export function listenForInstall(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    installAvailable.value = true;
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installAvailable.value = false;
  });
}

/** Show the browser's install dialog; resolves true if the player accepted. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const e = deferred;
  deferred = null;
  installAvailable.value = false;
  await e.prompt();
  return (await e.userChoice).outcome === 'accepted';
}

/** Running as an installed app (home-screen icon). */
export function isStandalone(): boolean {
  return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** iPhone or iPad Safari, where installing is Share → Add to Home Screen. */
export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
